import React from 'react';
import type { MSlotProps } from './MSlot.types';
import { ResponsiveGrid } from '@adobe/aem-react-editable-components';
import clsx from 'clsx';

// ResponsiveGrid accepts pagePath/itemPath at runtime (read by the AEM SPA SDK)
// but they are absent from its published TypeScript types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AEMResponsiveGrid = ResponsiveGrid as React.ComponentType<any>;

/**
 * @description This renders a Generic parsys component
 * @returns {React.ReactElement}
 */
export const MSlot: React.FC<MSlotProps> = props => {
  const {
    pagePath,
    itemPath,
    isAuthorMode = false,
    cqItems = {},
    className = '',
    containerClassName = '',
    model,
    aemReactComponentMapping = {},
    resourceType,
    cqPath,
  } = props;

  const renderComponents = () => {
    const components: React.ReactNode[] = [];
    const itemOrder = model?.[':order'] || Object.keys(cqItems);

    itemOrder.forEach((itemKey: string) => {
      const item = cqItems[itemKey];
      const componentType = item?.[':type'];
      const Component = aemReactComponentMapping[componentType];

      components.push(Component ? <Component key={itemKey} model={item} {...item} /> : null);
    });

    return components;
  };

  const renderHwsComponents = () => {
    const components: React.ReactNode[] = [];
    const itemOrder = model?.[':order'] || Object.keys(cqItems);

    itemOrder.forEach((itemKey: string) => {
      const item = cqItems[itemKey];
      const componentType = item?.[':type'];
      const Component = aemReactComponentMapping[componentType];

      // cqPath is pre-built by renderHwsSlotSlides in OCardCarousel —
      // no reconstruction needed here.
      components.push(
        Component ? <Component key={itemKey} model={item} {...item} pagePath={pagePath} cqPath={cqPath} /> : null
      );
    });

    return components;
  };

  if (isAuthorMode) {
    const hasAuthoredContent = Object.keys(cqItems).length > 0;

    if (hasAuthoredContent && resourceType) {
      // Render via renderHwsComponents(). Each design-system component (e.g. OBannerText)
      // wraps itself with EditableComponent, which attaches a cq-Overlay edit bar using childCqPath.
      return (
        <div className={clsx('m-slot', containerClassName)} data-test={JSON.stringify({ pagePath, itemPath })}>
          <div className={className}>{renderHwsComponents()}</div>
        </div>
      );
    }

    // Empty slot — show drag-drop zone so authors can drop a new component.
    return (
      <div className={clsx('m-slot', containerClassName)} data-test={JSON.stringify({ pagePath, itemPath })}>
        <div className={className}>
          <AEMResponsiveGrid
            pagePath={pagePath}
            itemPath={itemPath}
            columnCount="12"
            gridClassNames={''}
            config={{
              isEmpty: () => true,
              resourceType: resourceType ?? 'mi-aem-common-spa/components/container',
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={clsx('m-slot', containerClassName)} data-test={JSON.stringify({ pagePath, itemPath })}>
      <div className={className}>{renderComponents()}</div>
    </div>
  );
};

export default MSlot;
